import { useState, useMemo, useCallback, memo, forwardRef } from 'react';
import { useTranslation} from 'react-i18next';

import TaskDNDDraggable from './TaskDNDDraggable';
import Task from './task';

import { Task as TaskType, TaskDragProps, TaskGroupTaskProps, TasksViewConfigType } from 'types';

interface TasksListProps {
    tasks: string[],
    groupId: string,
    taskGroupType?: string,
    currentUserId?: string,
    simpleTask?: boolean,
    draggedTaskId?: string,
    showMessageWhenEmpty: boolean,
    maxVisibleTasks: number,
    virtualHeight?: number,
    rowVirtualizer?: any,
    noFooter: boolean,
    viewType: TasksViewConfigType,
    milestoneId: string,
    showProjectOnCard: boolean,
    hiddenKanbanFields: string[],
    withoutDnd: boolean,
    draggable: boolean,
    withAddOverlay?: boolean,
    meetingId?: string,
    isPublic?: boolean,
    isStatic?: boolean,
    labelsFetched?: boolean,
    taskProps?: TaskGroupTaskProps,
    onTaskClick: (niceId: string) => void,
    onTaskDragStart: (taskProps: TaskDragProps) => void,
    onTaskDragEnd: (taskProps: TaskDragProps) => void,
    onTaskMove: ({ source: any, destination: any, save: boolean }) => void,
    onTaskSelect: (task: TaskType, { metaKey: boolean, shifKey: boolean, select: boolean }) => void
};

const Row = memo(forwardRef(({ taskId, index, offsetTop, selectedTasks, currentTaskId, withoutDnd, sharedTaskProps, onDragStart, onDragEnd, moveTask }, ref) => {
    const style = useMemo(() => {
        if (typeof offsetTop === 'undefined') {
            return undefined;
        }

        return {
            position: 'absolute',
            top: 0,
            left: 2,
            right: 2,
            transform: `translateY(${offsetTop}px)`
        };
    }, [offsetTop]);

    const props = {
        id: taskId,
        index,
        style,
        selected: selectedTasks?.some(task => task.id === taskId),
        current: currentTaskId === taskId,
        ref,
        onMeasureChange: ref,
        ...sharedTaskProps
    }

    if (withoutDnd) {
        return <Task {...props} />;
    }

    return <TaskDNDDraggable {...props} moveTask={moveTask} onDragStart={onDragStart} onDragEnd={onDragEnd} />;
}));

const TasksList = ({ tasks, groupId, taskGroupType, currentTaskId, simpleTask, draggedTaskId, showMessageWhenEmpty = true, maxVisibleTasks = Number.POSITIVE_INFINITY, rowVirtualizer, selectedTasks, noFooter, viewType, milestoneId, showProjectOnCard, hiddenKanbanFields, withoutDnd, draggable, withAddOverlay, meetingId, isPublic, isStatic, labelsFetched, taskProps, onTaskClick, onTaskDragStart, onTaskDragEnd, onTaskMove, onTaskSelect }: TasksListProps) => {
    const [showAll, setShowAll] = useState(false);
    const { t } = useTranslation(['task', 'common']);

    const currentVisibleCount = useMemo(() => {
        const currentVisibleCount = tasks.length;

        if ((currentVisibleCount < maxVisibleTasks) || showAll) {
            return currentVisibleCount;
        }

        return maxVisibleTasks;
    }, [tasks, maxVisibleTasks, showAll]);

    const visibleTasks = useMemo(() => {
        if (currentVisibleCount < tasks.length) {
            return tasks.slice(0, currentVisibleCount);
        }

        return tasks;
    }, [tasks, currentVisibleCount]);

    const moveTask = useCallback(({ source, destination, save }: { source: any, destination: any, save: boolean }) => {
        if (!onTaskMove) {
            return;
        }

        requestAnimationFrame(() => {
            onTaskMove({
                source,
                destination,
                save
            });
        });
    }, [onTaskMove]);

    const handleShowMoreClick = useCallback(() => {
        setShowAll(true);
    }, []);

    const handleShowLessClick = useCallback(() => {
        setShowAll(false);
    }, []);

    const handleTaskDragStart = useCallback(taskProps => {
        onTaskDragStart?.(taskProps);
    }, [onTaskDragStart]);

    const handleTaskDragEnd = useCallback(taskProps => {
        onTaskDragEnd?.(taskProps);
    }, [onTaskDragEnd]);

    const createPlaceholder = useCallback(() => {
        if (isPublic) {
            return (
                <div className="task">
                    <div className="task-inner task-cell only-child">
                        <div className="task-cell-inner">
                            <div className="task-name-alt">
                                { t('noEntries.noOpenTasks') }
                            </div>
                        </div>
                    </div>
                </div>
            );
        }

        const props = {
            key: 'empty',
            index: 0,
            viewType,
            name: t('noEntries.noTasksDND'),
            taskGroupId: groupId,
            milestoneId,
            placeholder: true,
            completed: taskGroupType === 'completed',
            archived: taskGroupType === 'archived',
            showProject: showProjectOnCard,
            simpleTask,
            hiddenKanbanFields
        }


        if (withoutDnd) {
            return <Task {...props} />
        }

        return (
            <>
                {
                    withoutDnd ? (
                        <Task {...props} />
                    ) : (
                        <TaskDNDDraggable {...props} moveTask={moveTask} onDragStart={handleTaskDragStart} onDragEnd={handleTaskDragEnd} />
                    )
                }
            </>
        );
    }, [isPublic, viewType, groupId, milestoneId, showProjectOnCard, simpleTask, hiddenKanbanFields, withoutDnd, taskGroupType, moveTask, handleTaskDragStart, handleTaskDragEnd]);

    const sharedTaskProps = useMemo(() => {
        return {
            publicPage: isPublic,
            milestoneId,
            taskGroupId: groupId,
            taskGroupTaskProps: taskProps,
            simpleTask,
            draggedTask: draggedTaskId,
            showProject: showProjectOnCard,
            labelsFetched,
            draggable,
            meetingId,
            withAddOverlay,
            hiddenKanbanFields,
            onClick: onTaskClick,
            onSelect: onTaskSelect,
        }
    }, [isPublic, milestoneId, groupId, taskProps, simpleTask, draggedTaskId, showProjectOnCard, labelsFetched, draggable, meetingId, withAddOverlay, hiddenKanbanFields, onTaskClick, onTaskSelect]);

    const virtualHeight = rowVirtualizer?.getTotalSize();

    return (
        <div className={'task-list' + (noFooter ? ' no-footer' : '')} style={virtualHeight && visibleTasks.length ? { height: virtualHeight } : undefined}>
            <div className="group-items">
                {
                    visibleTasks.length ? (
                        <>
                            {
                                rowVirtualizer ? (
                                    rowVirtualizer.getVirtualItems().map(virtualRow => <Row
                                        ref={rowVirtualizer.measureElement}
                                        key={virtualRow.key}
                                        taskId={visibleTasks[virtualRow.index]}
                                        index={virtualRow.index}
                                        offsetTop={virtualRow.start}
                                        selectedTasks={selectedTasks}
                                        currentTaskId={currentTaskId}
                                        withoutDnd={withoutDnd}
                                        sharedTaskProps={sharedTaskProps}
                                        onDragStart={handleTaskDragStart}
                                        onDragEnd={handleTaskDragEnd}
                                        moveTask={moveTask}
                                    />)
                                ) : (
                                    visibleTasks.map((taskId, index) => <Row
                                        key={taskId}
                                        taskId={taskId}
                                        index={index}
                                        selectedTasks={selectedTasks}
                                        currentTaskId={currentTaskId}
                                        withoutDnd={withoutDnd}
                                        sharedTaskProps={sharedTaskProps}
                                        onDragStart={handleTaskDragStart}
                                        onDragEnd={handleTaskDragEnd}
                                        moveTask={moveTask}
                                    />)
                                )
                            }
                        </>
                    ) : isStatic ? (
                        <>
                            {
                                showMessageWhenEmpty ? (
                                    <TaskDNDDraggable
                                        isStatic={true}
                                        viewType={viewType}
                                        name={t('noEntries.noTasksByType', { type: taskGroupType === 'archived' ? t('common:Archived') : (taskGroupType === 'completed' ? t('common:Completed') : t('common:Open')).toLowerCase() })}
                                        taskGroupId={groupId}
                                        taskGroupTaskProps={taskProps}
                                        milestoneId={milestoneId}
                                        placeholder={true}
                                        completed={taskGroupType === 'completed'}
                                        archived={taskGroupType === 'archived'}
                                        showProject={showProjectOnCard}
                                        simpleTask={simpleTask}
                                        hiddenKanbanFields={hiddenKanbanFields}
                                        moveTask={moveTask}
                                        onDragStart={handleTaskDragStart}
                                        onDragEnd={handleTaskDragEnd}
                                    />
                                ) : null
                            }
                        </>
                    ) : createPlaceholder()
                }
            </div>

            {
                tasks.length > maxVisibleTasks ? (
                    <div className="group-hidden-items">
                        {
                            !showAll ? (
                                <strong onClick={handleShowMoreClick}>+ { t('common:more_count', { count: tasks.length - maxVisibleTasks }) }</strong>
                            ) : (
                                <strong onClick={handleShowLessClick}>{ t('common:Show less') }</strong>
                            )
                        }
                    </div>
                ) : null
            }
        </div>
    );
}

export default TasksList;
